import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Edit2, Check, X } from 'lucide-react';
import Button from '../shared/Button';
import { formatCurrency } from '../../utils/currency';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent';

const InquiryListItem = ({ item, onUpdate, onRemove, isEditable, showQuotedPrice }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedQuantity, setEditedQuantity] = useState(item.quantity);
  const [editedNotes, setEditedNotes] = useState(item.customer_notes || '');

  const handleSave = () => {
    onUpdate(item.id, {
      quantity: editedQuantity,
      customer_notes: editedNotes || null
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedQuantity(item.quantity);
    setEditedNotes(item.customer_notes || '');
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-white border border-gray-200 rounded-lg shadow-sm p-4"
    >
      {isEditing ? (
        // Edit mode
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Part Number</div>
            <div className="text-gray-900">{item.part_number}</div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Description</div>
            <div className="text-gray-600 text-sm">{item.description}</div>
          </div>

          <div>
            <label htmlFor={`item-qty-${item.id}`} className="block text-sm font-medium text-gray-700 mb-1">
              Quantity
            </label>
            <input
              id={`item-qty-${item.id}`}
              type="number"
              min="1"
              value={editedQuantity}
              onChange={(e) => setEditedQuantity(parseInt(e.target.value) || 1)}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor={`item-notes-${item.id}`} className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              id={`item-notes-${item.id}`}
              value={editedNotes}
              onChange={(e) => setEditedNotes(e.target.value)}
              placeholder="Add any special requirements or notes..."
              rows={2}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={handleSave} className="flex-1" icon={<Check className="w-4 h-4" aria-hidden="true" />}>
              Save
            </Button>
            <Button variant="secondary" size="sm" onClick={handleCancel} className="flex-1" icon={<X className="w-4 h-4" aria-hidden="true" />}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        // View mode
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="font-semibold text-gray-900 mb-1">
                {item.part_number}
              </div>
              <div className="text-sm text-gray-600">
                {item.description}
              </div>
            </div>

            {isEditable && (
              <div className="flex gap-1 ml-2">
                <Button
                  variant="ghost"
                  onClick={() => setIsEditing(true)}
                  ariaLabel={`Edit ${item.part_number}`}
                  icon={<Edit2 className="w-4 h-4" aria-hidden="true" />}
                />
                <Button
                  variant="ghostDanger"
                  onClick={() => {
                    if (window.confirm(`Remove ${item.part_number} from this inquiry?`)) {
                      onRemove(item.id);
                    }
                  }}
                  ariaLabel={`Remove ${item.part_number}`}
                  icon={<Trash2 className="w-4 h-4" aria-hidden="true" />}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
            <div>
              <div className="text-xs text-gray-500 mb-1">Quantity</div>
              <div className="font-medium text-gray-900 tabular-nums">{item.quantity}</div>
            </div>

            {showQuotedPrice && item.quoted_price !== null && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Quoted Price</div>
                <div className="font-semibold text-gray-900 tabular-nums">
                  {formatCurrency(parseFloat(item.quoted_price))}
                </div>
              </div>
            )}
          </div>

          {item.customer_notes && (
            <div className="pt-3 border-t border-gray-100">
              <div className="text-xs text-gray-500 mb-1">Notes</div>
              <div className="text-sm text-gray-700">{item.customer_notes}</div>
            </div>
          )}

          {item.staff_notes && (
            <div className="pt-3 border-t border-gray-100">
              <div className="text-xs text-gray-500 mb-1">Staff Notes</div>
              <div className="text-sm text-blue-700 bg-blue-50 p-2 rounded-lg">
                {item.staff_notes}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default InquiryListItem;
